/**
 * Protected Route Component
 * 
 * Componente para proteger rutas que requieren autenticación
 */

import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'

export interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles?: string[]
  requireAnyRole?: boolean
  redirectTo?: string
}

export function ProtectedRoute({
  children,
  requiredRoles = [],
  requireAnyRole = false,
  redirectTo = '/login',
}: ProtectedRouteProps) {
  const { isAuthenticated, user, isLoading, refreshUser } = useAuth()
  const location = useLocation()
  const [hasCheckedSession, setHasCheckedSession] = useState(false)

  // Verificar si hay una sesión válida antes de redirigir
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !hasCheckedSession) {
      // Esperar un poco para dar tiempo a que el estado se actualice
      const timer = setTimeout(async () => {
        try {
          const { fetchAuthSession } = await import('aws-amplify/auth')
          const session = await fetchAuthSession({ forceRefresh: false })
          if (session.tokens) {
            // Hay tokens, intentar refrescar el usuario
            console.log('[ProtectedRoute] Found tokens, refreshing user state...')
            await refreshUser()
          }
        } catch (err) {
          console.warn('[ProtectedRoute] Error checking session:', err)
        } finally {
          setHasCheckedSession(true)
        }
      }, 100) // Pequeño delay para evitar condiciones de carrera
      
      return () => clearTimeout(timer)
    } else if (isAuthenticated) {
      setHasCheckedSession(true)
    }
  }, [isLoading, isAuthenticated, hasCheckedSession, refreshUser])

  // Debug logging
  console.log('[ProtectedRoute] State:', { isAuthenticated, isLoading, hasUser: !!user, userRoles: user?.roles, requiredRoles, hasCheckedSession })

  // Mostrar loading mientras se carga la autenticación
  if (isLoading || (!isAuthenticated && !hasCheckedSession)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!isAuthenticated) {
    console.log('[ProtectedRoute] Not authenticated, showing error', {
      isLoading,
      hasUser: !!user,
      userEmail: user?.email,
      userRoles: user?.roles,
      location: location.pathname,
      hasCheckedSession
    })
    
    // Si estamos en desarrollo y hay un usuario pero sin roles, mostrar información útil
    if (user && !isLoading && requiredRoles.length > 0) {
      const userRoles = (user.roles || []).map(r => String(r).toLowerCase().trim())
      const hasRequiredRole = requiredRoles.some(role => 
        userRoles.includes(String(role).toLowerCase().trim())
      )
      
      if (!hasRequiredRole) {
        // Mostrar mensaje de ayuda antes de redirigir
        return (
          <div className="min-h-screen flex items-center justify-center bg-tf-bg-muted p-4">
            <div className="max-w-md w-full bg-tf-bg-card shadow-lg rounded-lg p-6">
              <h1 className="text-2xl font-bold text-tf-feedback-error-text mb-4">Acceso Denegado</h1>
              <p className="text-tf-text-sub mb-4">
                No tienes los permisos necesarios para acceder a esta página.
              </p>
              <div className="bg-tf-bg-muted p-4 rounded mb-4">
                <p className="text-sm font-medium text-tf-text-sub mb-2">Información:</p>
                <ul className="text-sm text-tf-text-muted space-y-1">
                  <li>Email: {user.email}</li>
                  <li>Roles actuales: {userRoles.length > 0 ? userRoles.join(', ') : 'Ninguno'}</li>
                  <li>Roles requeridos: {requiredRoles.join(', ')}</li>
                </ul>
              </div>
              <div className="bg-tf-feedback-info-bg p-4 rounded mb-4">
                <p className="text-sm font-medium text-tf-feedback-info-text mb-2">Solución:</p>
                <ol className="text-sm text-tf-feedback-info-text list-decimal list-inside space-y-1">
                  <li>Ejecuta: <code className="bg-tf-primary-lighter px-1 rounded">./scripts/add-user-to-admin-group.sh {user.email} dev</code></li>
                  <li>Cierra sesión y vuelve a iniciar sesión</li>
                </ol>
              </div>
              <div className="flex gap-3">
                <a
                  href="/auth-debug"
                  className="flex-1 text-center px-4 py-2 bg-tf-bg-hover text-tf-text rounded hover:bg-tf-bg-pressed transition"
                >
                  Ver Diagnóstico
                </a>
                <button
                  onClick={() => window.location.href = redirectTo}
                  className="flex-1 px-4 py-2 bg-primary text-white rounded hover:bg-primary/90 transition"
                >
                  Ir a Login
                </button>
              </div>
            </div>
          </div>
        )
      }
    }
    
    return <Navigate to={redirectTo} state={{ from: location }} replace />
  }

  // Verificar roles si se especifican
  if (requiredRoles.length > 0 && user) {
    const userRoles = (user.roles || []).map(r => String(r).toLowerCase().trim())
    const normalizedRequiredRoles = requiredRoles.map(r => String(r).toLowerCase().trim())
    
    console.log('[ProtectedRoute] Role check:', { 
      userRoles: userRoles, 
      requiredRoles: normalizedRequiredRoles,
      requireAnyRole: requireAnyRole,
      userRolesString: JSON.stringify(userRoles),
      requiredRolesString: JSON.stringify(normalizedRequiredRoles),
      originalUserRoles: user.roles,
      originalRequiredRoles: requiredRoles
    })
    
    const hasAccess = requireAnyRole
      ? normalizedRequiredRoles.some(role => userRoles.includes(role))
      : normalizedRequiredRoles.every(role => userRoles.includes(role))

    if (!hasAccess) {
      console.log('[ProtectedRoute] Access denied - missing required roles', { 
        userRoles: userRoles, 
        requiredRoles: requiredRoles,
        userRolesArray: Array.isArray(userRoles) ? userRoles : [userRoles],
        comparison: requiredRoles.map(r => ({ role: r, hasRole: userRoles.includes(r) }))
      })
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-critical mb-2">Acceso Denegado</h1>
            <p className="text-text-subdued">
              No tienes permisos para acceder a esta página.
            </p>
            <p className="text-sm text-text-subdued mt-2">
              Roles requeridos: {requiredRoles.join(', ')}
            </p>
            <p className="text-sm text-text-subdued">
              Tus roles: {userRoles.length > 0 ? userRoles.join(', ') : 'Ninguno'}
            </p>
          </div>
        </div>
      )
    }
  }

  console.log('[ProtectedRoute] Access granted')
  return <>{children}</>
}







